import { useEffect, useMemo, useState } from 'react';
import { speak } from '../../utils/speech.js';
import { BALL } from '../../theme.js';

const T = BALL;

export default function Match({ exercise, lang, onResult, locked }) {
  const sourceKey = lang;

  // Both columns are shuffled independently per mount so the pairs aren't
  // visually aligned — pure top-to-bottom matching becomes trivial otherwise.
  const leftItems = useMemo(() => {
    const arr = exercise.items.map((it) => ({ key: it.hr, label: it.hr, item: it }));
    return arr.map((x) => ({ x, r: Math.random() })).sort((a, b) => a.r - b.r).map((p) => p.x);
  }, [exercise]);

  const rightItems = useMemo(() => {
    const arr = exercise.items.map((it) => ({
      key: it.hr, label: it[sourceKey], item: it,
    }));
    return arr.map((x) => ({ x, r: Math.random() })).sort((a, b) => a.r - b.r).map((p) => p.x);
  }, [exercise, sourceKey]);

  const [matched, setMatched] = useState(new Set());
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [selectedRight, setSelectedRight] = useState(null);
  const [wrong, setWrong] = useState(null);

  useEffect(() => {
    setMatched(new Set());
    setSelectedLeft(null);
    setSelectedRight(null);
    setWrong(null);
  }, [exercise]);

  useEffect(() => {
    if (selectedLeft && selectedRight) {
      if (selectedLeft === selectedRight) {
        setMatched((m) => new Set([...m, selectedLeft]));
        setSelectedLeft(null);
        setSelectedRight(null);
      } else {
        setWrong({ left: selectedLeft, right: selectedRight });
        const tid = setTimeout(() => {
          setWrong(null);
          setSelectedLeft(null);
          setSelectedRight(null);
        }, 500);
        return () => clearTimeout(tid);
      }
    }
  }, [selectedLeft, selectedRight]);

  useEffect(() => {
    if (matched.size === exercise.items.length) {
      const tid = setTimeout(() => onResult({ correct: true, given: '', expected: '' }), 250);
      return () => clearTimeout(tid);
    }
  }, [matched, exercise, onResult]);

  function cellStyle(side, key) {
    let borderColor = T.hairline;
    let textColor = T.ink;
    let shadowColor = T.hairline;
    let opacity = 1;

    if (matched.has(key)) {
      borderColor = T.mint;
      textColor = T.mint;
      shadowColor = 'transparent';
      opacity = 0.35;
    } else if ((side === 'left' && wrong?.left === key) || (side === 'right' && wrong?.right === key)) {
      borderColor = T.red;
      textColor = T.red;
      shadowColor = T.redDeep;
    } else if ((side === 'left' && selectedLeft === key) || (side === 'right' && selectedRight === key)) {
      borderColor = T.blue;
      textColor = T.blue;
      shadowColor = T.blueDeep;
    }

    return {
      appearance: 'none', cursor: locked ? 'not-allowed' : 'pointer',
      background: T.bgAlt,
      border: `2px solid ${borderColor}`,
      boxShadow: matched.has(key) ? 'none' : `0 3px 0 ${shadowColor}`,
      color: textColor, opacity,
      borderRadius: 14, height: 56,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 800, fontSize: 17, fontFamily: 'inherit',
      transition: 'border-color 120ms, color 120ms, opacity 200ms',
    };
  }

  return (
    <div style={{ padding: '4px 22px 0', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <div style={{
          fontSize: 11, fontWeight: 800, color: T.gold, letterSpacing: '0.12em',
        }}>{lang === 'en' ? 'MATCH PAIRS' : 'PARA IHOP PAREN'}</div>
        <h2 style={{
          fontSize: 24, fontWeight: 900, color: T.ink, margin: '6px 0 0', lineHeight: 1.15,
        }}>{lang === 'en' ? 'Tap the matching pairs' : 'Tryck på matchande par'}</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {leftItems.map(({ key, label, item }) => (
            <button
              key={`l-${key}`}
              type="button"
              disabled={locked || matched.has(key)}
              onClick={() => {
                speak(item.hr);
                setSelectedLeft(key);
              }}
              style={cellStyle('left', key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rightItems.map(({ key, label }) => (
            <button
              key={`r-${key}`}
              type="button"
              disabled={locked || matched.has(key)}
              onClick={() => setSelectedRight(key)}
              style={cellStyle('right', key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
